import { useState } from 'react';
import {
  Loader2, Search, Eye, Play, AlertTriangle, CheckCircle2,
  ChevronDown, ChevronRight, XCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmt(n) {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency', currency: 'GBP', minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(n);
}

const RESOLUTION_BADGE = {
  resolved: { label: 'Resolved', cls: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
  ambiguous: { label: 'Ambiguous', cls: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' },
  unresolvable: { label: 'Unresolvable', cls: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
};

function ResolutionTable({ properties }) {
  const [open, setOpen] = useState(false);
  if (!properties || properties.length === 0) return null;

  return (
    <div className="border rounded-md">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 w-full px-3 py-1.5 text-xs font-medium text-left hover:bg-muted/50"
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        All properties ({properties.length})
      </button>
      {open && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-t bg-muted/30">
                <th className="px-2 py-1 text-left font-medium">Property ID</th>
                <th className="px-2 py-1 text-left font-medium">Name</th>
                <th className="px-2 py-1 text-left font-medium">Country</th>
                <th className="px-2 py-1 text-center font-medium">Bookings</th>
                <th className="px-2 py-1 text-right font-medium">Supplier Total</th>
                <th className="px-2 py-1 text-left font-medium">Resolution</th>
                <th className="px-2 py-1 text-left font-medium">Path</th>
                <th className="px-2 py-1 text-left font-medium">Supplier Found</th>
              </tr>
            </thead>
            <tbody>
              {properties.map((p) => {
                const badge = RESOLUTION_BADGE[p.resolution] || {};
                return (
                  <tr key={p.property_id} className="border-t">
                    <td className="px-2 py-1 font-mono text-xs">{p.property_id}</td>
                    <td className="px-2 py-1">{p.property_name || '-'}</td>
                    <td className="px-2 py-1">{p.country || '-'}</td>
                    <td className="px-2 py-1 text-center tabular-nums">{p.booking_count}</td>
                    <td className="px-2 py-1 text-right font-mono">{fmt(p.supplier_total_gbp)}</td>
                    <td className="px-2 py-1">
                      <Badge className={badge.cls}>{badge.label}</Badge>
                    </td>
                    <td className="px-2 py-1 text-xs">{p.resolution_path || '-'}</td>
                    <td className="px-2 py-1">
                      {p.resolved_supplier_name || p.resolved_supplier_id || (
                        p.candidates ? p.candidates.join(', ') : '-'
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function BackfillPreviewTable({ properties }) {
  if (!properties || properties.length === 0) return null;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b bg-muted/30">
            <th className="px-2 py-1 text-left font-medium">Property ID</th>
            <th className="px-2 py-1 text-left font-medium">Name</th>
            <th className="px-2 py-1 text-left font-medium">Supplier ID (write)</th>
            <th className="px-2 py-1 text-left font-medium">Supplier Name (write)</th>
            <th className="px-2 py-1 text-left font-medium">Path</th>
            <th className="px-2 py-1 text-right font-medium">Supplier Total</th>
          </tr>
        </thead>
        <tbody>
          {properties.map((p) => (
            <tr key={p.property_id} className="border-t">
              <td className="px-2 py-1 font-mono text-xs">{p.property_id}</td>
              <td className="px-2 py-1">{p.property_name || '-'}</td>
              <td className="px-2 py-1 font-mono text-green-700 dark:text-green-400">
                {p.supplier_id_to_write || <span className="text-muted-foreground italic">none</span>}
              </td>
              <td className="px-2 py-1 font-medium text-green-700 dark:text-green-400">
                {p.supplier_name_to_write || <span className="text-muted-foreground italic">none</span>}
              </td>
              <td className="px-2 py-1 text-xs">{p.resolution_path || '-'}</td>
              <td className="px-2 py-1 text-right font-mono">{fmt(p.supplier_total_gbp)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function NoSupplierPropertyResolverPanel() {
  const [checking, setChecking] = useState(false);
  const [audit, setAudit] = useState(null);

  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);

  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);

  const [error, setError] = useState(null);

  // Phase 1: Run audit
  const runCheck = async () => {
    setChecking(true);
    setError(null);
    setAudit(null);
    setPreview(null);
    setApplyResult(null);
    try {
      const { data } = await diagnosticsAPI.noSupplierPropertyResolver();
      setAudit(data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setChecking(false);
    }
  };

  // Phase 2: Preview backfill
  const runPreview = async () => {
    setPreviewing(true);
    setPreview(null);
    setApplyResult(null);
    try {
      const { data } = await diagnosticsAPI.noSupplierBackfillPreview();
      setPreview(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewing(false);
    }
  };

  // Phase 2: Apply backfill
  const runApply = async () => {
    setApplying(true);
    try {
      const { data } = await diagnosticsAPI.noSupplierBackfillApply();
      setApplyResult(data);
      setPreview(null);
      if (data.updated > 0) {
        toast.success(data.message);
      }
      if (data.skipped_conflict > 0) {
        toast.error(`${data.skipped_conflict} conflict(s) flagged.`);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  };

  const canPreview = audit && audit.summary?.resolved > 0;
  const canApply = preview && preview.safe_to_apply && !applying;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">No-Supplier Property Resolver</CardTitle>
          <Button variant="outline" size="sm" onClick={runCheck} disabled={checking || previewing || applying}>
            {checking ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            Check
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Identifies properties with no supplier_id/supplier_name, attempts resolution by
          re-fetching from the BR OS API and cross-referencing supplier invoices.
          Phase 2 backfills resolved suppliers onto properties (preview, then confirm).
        </p>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Idle state */}
        {!audit && !error && !checking && (
          <p className="text-sm text-muted-foreground">
            Press Check to audit no-supplier properties and attempt supplier resolution.
          </p>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-start gap-2 text-sm text-red-500">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Phase 1: Audit results */}
        {audit && (
          <div className="space-y-4">
            {/* Clean state */}
            {audit.status === 'clean' && (
              <div className="rounded-lg border p-4 text-center">
                <CheckCircle2 className="h-6 w-6 text-green-500 mx-auto mb-1" />
                <p className="text-sm text-muted-foreground">
                  No properties with missing supplier found.
                </p>
              </div>
            )}

            {/* Audit summary */}
            {audit.status === 'audit_complete' && (
              <>
                {/* API warning */}
                {!audit.api_available && (
                  <div className="rounded-md border border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/20 px-3 py-2 text-xs text-yellow-800 dark:text-yellow-300 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    BR OS API was unreachable. Resolution results may be incomplete.
                  </div>
                )}

                {/* Headline */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    {audit.total_properties} properties, {audit.total_bookings} bookings
                  </span>
                  <div className="text-right">
                    <div className="text-xl font-bold font-mono">{fmt(audit.total_gbp)}</div>
                    <div className="text-xs text-muted-foreground">total no-supplier exposure</div>
                  </div>
                </div>

                {/* Summary breakdown */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">Resolved</p>
                    <p className="text-xl font-bold tabular-nums text-green-600 dark:text-green-400">
                      {audit.summary.resolved}
                    </p>
                    <p className="text-xs font-mono text-muted-foreground">
                      {fmt(audit.summary_gbp.resolved)}
                    </p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">Ambiguous</p>
                    <p className="text-xl font-bold tabular-nums text-yellow-600 dark:text-yellow-400">
                      {audit.summary.ambiguous}
                    </p>
                    <p className="text-xs font-mono text-muted-foreground">
                      {fmt(audit.summary_gbp.ambiguous)}
                    </p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">Unresolvable</p>
                    <p className="text-xl font-bold tabular-nums text-red-600 dark:text-red-400">
                      {audit.summary.unresolvable}
                    </p>
                    <p className="text-xs font-mono text-muted-foreground">
                      {fmt(audit.summary_gbp.unresolvable)}
                    </p>
                  </div>
                </div>

                {/* Outliers */}
                {audit.outliers?.length > 0 && (
                  <div className="rounded-md border border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/20 px-3 py-2 space-y-2">
                    <p className="text-xs font-medium text-orange-700 dark:text-orange-400 flex items-center gap-1.5">
                      <AlertTriangle className="h-4 w-4" />
                      Outliers ({audit.outliers.length})
                    </p>
                    {audit.outliers.map((o) => (
                      <div key={o.property_id} className="text-xs text-orange-800 dark:text-orange-300">
                        <span className="font-mono">{o.property_id}</span>
                        {o.property_name && <span className="ml-1">({o.property_name})</span>}
                        <span className="ml-1 font-mono font-semibold">{fmt(o.supplier_total_gbp)}</span>
                        <span className="ml-1">{o.booking_count} booking(s)</span>
                        <ul className="ml-4 list-disc mt-0.5">
                          {o.reasons.map((r, i) => <li key={i}>{r}</li>)}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}

                {/* Per-property table */}
                <ResolutionTable properties={audit.properties} />

                {/* Phase 2: Backfill controls */}
                {canPreview && (
                  <div className="border-t pt-4 space-y-3">
                    <h3 className="text-sm font-semibold">Backfill Supplier</h3>
                    <div className="flex gap-2">
                      <Button
                        variant="default"
                        size="sm"
                        onClick={runPreview}
                        disabled={previewing || applying}
                      >
                        {previewing ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Eye className="h-4 w-4 mr-2" />
                        )}
                        Preview Backfill
                      </Button>
                      {canApply && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={runApply}
                          disabled={applying}
                        >
                          {applying ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          ) : (
                            <Play className="h-4 w-4 mr-2" />
                          )}
                          Apply Backfill
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Phase 2: Preview results */}
        {preview && (
          <div className="space-y-3">
            <div className={`rounded-lg border p-3 ${
              preview.safe_to_apply
                ? 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800'
                : 'bg-yellow-50 dark:bg-yellow-950/30 border-yellow-200 dark:border-yellow-800'
            }`}>
              <div className="flex items-center gap-2">
                {preview.safe_to_apply ? (
                  <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 shrink-0" />
                ) : (
                  <XCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-400 shrink-0" />
                )}
                <span className="text-sm font-medium">
                  {preview.safe_to_apply
                    ? `${preview.total_resolved} properties ready for backfill (${fmt(preview.total_resolved_gbp)})`
                    : 'No resolved properties to backfill.'}
                </span>
              </div>
            </div>
            {preview.safe_to_apply && (
              <BackfillPreviewTable properties={preview.properties} />
            )}
          </div>
        )}

        {/* Phase 2: Apply results */}
        {applyResult && (
          <div className="rounded-lg border border-green-300 dark:border-green-800 p-3 bg-green-50 dark:bg-green-950/30 space-y-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-sm font-medium">Backfill applied</span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">Updated</div>
                <div className="text-lg font-bold tabular-nums text-green-600">{applyResult.updated}</div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">Already populated</div>
                <div className="text-lg font-bold tabular-nums">{applyResult.skipped_already_populated}</div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">Conflicts</div>
                <div className={`text-lg font-bold tabular-nums ${applyResult.skipped_conflict > 0 ? 'text-red-600' : ''}`}>
                  {applyResult.skipped_conflict}
                </div>
              </div>
            </div>

            {/* Per-property results */}
            {applyResult.results?.length > 0 && (
              <details className="space-y-2">
                <summary className="text-sm font-semibold cursor-pointer">
                  Per-property results ({applyResult.results.length})
                </summary>
                <div className="overflow-x-auto mt-2">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="py-1 pr-3 font-medium">Property ID</th>
                        <th className="py-1 pr-3 font-medium">Action</th>
                        <th className="py-1 pr-3 font-medium">Supplier</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applyResult.results.map((r, i) => (
                        <tr key={i} className="border-b border-border/50">
                          <td className="py-1 pr-3 font-mono">{r.property_id}</td>
                          <td className="py-1 pr-3">
                            <Badge
                              variant={
                                r.action === 'updated' ? 'default' :
                                r.action === 'conflict' ? 'destructive' : 'outline'
                              }
                              className="text-[10px]"
                            >
                              {r.action}
                            </Badge>
                          </td>
                          <td className="py-1 pr-3">
                            {r.supplier_name || r.supplier_id || '-'}
                            {r.existing_supplier_name && (
                              <span className="text-red-600 ml-1">
                                (existing: {r.existing_supplier_name})
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
